import {
	useMemo,
	ChangeEvent,
	FunctionComponent,
	ComponentProps,
	useState,
	useEffect
} from 'react';

import {
	Chain,
	NonChain,
	InfoWhite,
	StaffGreen,
	StaffOrange,
	SalesVolumeMoney,
	SquareFeetIcon
} from '@/resources/assets/images';
import { Input } from '@/components/atoms/Input';
import {
	BusinessQuery,
	FilterQueryType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import Button from '@/components/Button/newButtons';
import { FilterComponents } from '../../molecules/FilterComponents';
import type { FilterProps } from '../../../@types';

export const FilterBusinessInfo = ({
	appliedFilters,
	onFilterChange
}: FilterProps) => {
	const [yelpMin, setYelpMin] = useState(appliedFilters?.minYelpReviews);
	const [yelpMax, setYelpMax] = useState(appliedFilters?.maxYelpReviews);

	useEffect(() => {
		setYelpMin(state =>
			state !== appliedFilters?.minYelpReviews
				? appliedFilters?.minYelpReviews
				: state
		);
	}, [appliedFilters?.minYelpReviews]);

	useEffect(() => {
		setYelpMax(state =>
			state !== appliedFilters?.maxYelpReviews
				? appliedFilters?.maxYelpReviews
				: state
		);
	}, [appliedFilters?.maxYelpReviews]);

	const filtersCount = useMemo(() => {
		const countGroups = [
			['chains', 'nonChains'],

			['countOne'],
			['countTwoTen'],
			['countTenPlus'],

			['minYelpReviews', 'maxYelpReviews'],
			['includePOIs', 'excludePOIs'],

			['salesVolumeLessThan5M'],
			['salesVolume5To10M'],
			['salesVolume10To20M'],
			['salesVolume20To50M'],
			['salesVolume50To100M'],
			['salesVolumeOver100M'],

			['employees1To49'],
			['employees50To99'],
			['employees100To249'],
			['employees250To499'],
			['employeesOver500'],

			['sqftSmall'],
			['sqftMedium'],
			['sqftLarge']
		];

		return countGroups.reduce((count, group) => {
			if (
				group.some(key => appliedFilters?.[key as keyof BusinessQuery])
			) {
				return count + 1;
			}
			return count;
		}, 0);
	}, [appliedFilters]);

	const handleClearBusinessInfoFilters = () => {
		const newFilters = {
			...appliedFilters,
			skip: 0,
			chains: undefined,
			nonChains: undefined,
			countOne: undefined,
			countTwoTen: undefined,
			countTenPlus: undefined,
			minYelpReviews: undefined,
			maxYelpReviews: undefined,
			includePOIs: undefined,
			excludePOIs: undefined,
			salesVolumeLessThan5M: undefined,
			salesVolume5To10M: undefined,
			salesVolume10To20M: undefined,
			salesVolume20To50M: undefined,
			salesVolume50To100M: undefined,
			salesVolumeOver100M: undefined,
			employees1To49: undefined,
			employees50To99: undefined,
			employees100To249: undefined,
			employees250To499: undefined,
			employeesOver500: undefined,
			sqftSmall: undefined,
			sqftMedium: undefined,
			sqftLarge: undefined
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User cleared business info filters',
			properties: { query: newFilters }
		});

		onFilterChange(newFilters);
	};

	const handleToggleFilter = ({ name, value }: ChangeFiltersProps) => {
		const newFilters = {
			...appliedFilters,
			skip: 0,
			limit: 50,
			queryType: FilterQueryType.FILTER,
			chains:
				name === 'chains'
					? value === 'true'
					: (appliedFilters?.chains ?? undefined),
			nonChains:
				name === 'chains'
					? value === 'false'
					: (appliedFilters?.nonChains ?? undefined),

			countOne:
				name === 'countOne'
					? !appliedFilters?.countOne
					: (appliedFilters?.countOne ?? undefined),
			countTwoTen:
				name === 'countTwoTen'
					? !appliedFilters?.countTwoTen
					: (appliedFilters?.countTwoTen ?? undefined),
			countTenPlus:
				name === 'countTenPlus'
					? !appliedFilters?.countTenPlus
					: (appliedFilters?.countTenPlus ?? undefined),
			includePOIs:
				name === 'POIs'
					? value === 'true'
					: (appliedFilters?.includePOIs ?? undefined),
			excludePOIs:
				name === 'POIs'
					? value === 'false'
					: (appliedFilters?.excludePOIs ?? undefined),

			salesVolumeLessThan5M:
				name === 'salesVolumeLessThan5M'
					? !appliedFilters?.salesVolumeLessThan5M
					: (appliedFilters?.salesVolumeLessThan5M ?? undefined),
			salesVolume5To10M:
				name === 'salesVolume5To10M'
					? !appliedFilters?.salesVolume5To10M
					: (appliedFilters?.salesVolume5To10M ?? undefined),
			salesVolume10To20M:
				name === 'salesVolume10To20M'
					? !appliedFilters?.salesVolume10To20M
					: (appliedFilters?.salesVolume10To20M ?? undefined),
			salesVolume20To50M:
				name === 'salesVolume20To50M'
					? !appliedFilters?.salesVolume20To50M
					: (appliedFilters?.salesVolume20To50M ?? undefined),
			salesVolume50To100M:
				name === 'salesVolume50To100M'
					? !appliedFilters?.salesVolume50To100M
					: (appliedFilters?.salesVolume50To100M ?? undefined),
			salesVolumeOver100M:
				name === 'salesVolumeOver100M'
					? !appliedFilters?.salesVolumeOver100M
					: (appliedFilters?.salesVolumeOver100M ?? undefined),

			employees1To49:
				name === 'employees1To49'
					? !appliedFilters?.employees1To49
					: (appliedFilters?.employees1To49 ?? undefined),
			employees50To99:
				name === 'employees50To99'
					? !appliedFilters?.employees50To99
					: (appliedFilters?.employees50To99 ?? undefined),
			employees100To249:
				name === 'employees100To249'
					? !appliedFilters?.employees100To249
					: (appliedFilters?.employees100To249 ?? undefined),
			employees250To499:
				name === 'employees250To499'
					? !appliedFilters?.employees250To499
					: (appliedFilters?.employees250To499 ?? undefined),
			employeesOver500:
				name === 'employeesOver500'
					? !appliedFilters?.employeesOver500
					: (appliedFilters?.employeesOver500 ?? undefined),

			sqftSmall:
				name === 'sqftSmall'
					? !appliedFilters?.sqftSmall
					: (appliedFilters?.sqftSmall ?? undefined),
			sqftMedium:
				name === 'sqftMedium'
					? !appliedFilters?.sqftMedium
					: (appliedFilters?.sqftMedium ?? undefined),
			sqftLarge:
				name === 'sqftLarge'
					? !appliedFilters?.sqftLarge
					: (appliedFilters?.sqftLarge ?? undefined)
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User changed filters',
			properties: {
				filterChanged: name,
				value: value ? 'Included' : 'Neutral',
				query: newFilters
			}
		});

		onFilterChange(newFilters);
	};

	const handleUpdateYelpValue = () => {
		if (!yelpMin && !yelpMax) {
			return;
		}

		const newQuery: BusinessQuery = {
			...appliedFilters,
			minYelpReviews: Number(yelpMin ?? 0),
			maxYelpReviews: Number(yelpMax ?? 0),
			skip: 0,
			limit: 50,
			queryType: FilterQueryType.FILTER
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User changed filters',
			properties: {
				filterChanged: 'Yelp Reviews',
				query: newQuery
			}
		});

		onFilterChange(newQuery);
	};

	const handleUpdateMinYelpValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		const isInvalidNumber = Number.isNaN(value);
		if (isInvalidNumber) return;

		setYelpMin(Number(value));
	};

	const handleUpdateMaxYelpValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		const isInvalidNumber = Number.isNaN(value);
		if (isInvalidNumber) return;

		setYelpMax(Number(value));
	};

	return (
		<FilterComponents.Wrapper moduleName="business-info">
			<FilterComponents.Header
				icon={InfoWhite}
				title="Business Info"
				showClearFiltersButton={filtersCount > 0}
				activeFiltersCount={filtersCount}
				onClearFilters={handleClearBusinessInfoFilters}
			/>

			<FilterComponents.Content>
				<StatusControl title="Chain">
					<RadioOption
						name="chains"
						label="Chain"
						value="true"
						icon={Chain}
						checked={appliedFilters?.chains || false}
						onChange={handleToggleFilter}
					/>
					<RadioOption
						name="chains"
						label="Non-chain"
						value="false"
						icon={NonChain}
						checked={appliedFilters?.nonChains || false}
						onChange={handleToggleFilter}
					/>
				</StatusControl>

				<StatusControl title="# of Locations">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<CheckboxOption
							name="countOne"
							label="1"
							checked={appliedFilters?.countOne || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="countTwoTen"
							label="2-10"
							checked={appliedFilters?.countTwoTen || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="countTenPlus"
							label="10+"
							checked={appliedFilters?.countTenPlus || false}
							onChange={handleToggleFilter}
						/>
					</div>
				</StatusControl>

				<StatusControl title="Online Review Count">
					<div className="flex gap-3">
						<Input
							placeholder="Min"
							size="sm"
							type="text"
							tabIndex={0}
							min="0"
							value={yelpMin}
							onChange={handleUpdateMinYelpValue}
						/>
						<Input
							placeholder="Max"
							size="sm"
							type="text"
							min="0"
							value={yelpMax}
							tabIndex={0}
							onChange={handleUpdateMaxYelpValue}
						/>
					</div>

					<Button
						className="border-none disabled:opacity-50"
						fullWidth
						size="small"
						disabled={!yelpMin && !yelpMax}
						onClick={handleUpdateYelpValue}
					>
						Apply
					</Button>
				</StatusControl>

				<StatusControl title="Has Staff Info">
					<RadioOption
						name="POIs"
						label="Yes"
						value="true"
						icon={StaffGreen}
						checked={appliedFilters?.includePOIs || false}
						onChange={handleToggleFilter}
					/>
					<RadioOption
						name="POIs"
						label="No"
						icon={StaffOrange}
						value="false"
						checked={appliedFilters?.excludePOIs || false}
						onChange={handleToggleFilter}
					/>
				</StatusControl>

				<StatusControl title="Sales Volume" icon={SalesVolumeMoney}>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<CheckboxOption
							name="salesVolumeLessThan5M"
							label="< $5M"
							checked={
								appliedFilters?.salesVolumeLessThan5M || false
							}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="salesVolume5To10M"
							label="$5-10M"
							checked={appliedFilters?.salesVolume5To10M || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="salesVolume10To20M"
							label="$10-20M"
							checked={
								appliedFilters?.salesVolume10To20M || false
							}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="salesVolume20To50M"
							label="$20-50M"
							checked={
								appliedFilters?.salesVolume20To50M || false
							}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="salesVolume50To100M"
							label="$50-100M"
							checked={
								appliedFilters?.salesVolume50To100M || false
							}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="salesVolumeOver100M"
							label="> $100M"
							checked={
								appliedFilters?.salesVolumeOver100M || false
							}
							onChange={handleToggleFilter}
						/>
					</div>
				</StatusControl>

				<StatusControl title="# of Employees">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<CheckboxOption
							name="employees1To49"
							label="1-49"
							checked={appliedFilters?.employees1To49 || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="employees50To99"
							label="50-99"
							checked={appliedFilters?.employees50To99 || false}
							onChange={handleToggleFilter}
						/>

						<CheckboxOption
							name="employees100To249"
							label="100-249"
							checked={appliedFilters?.employees100To249 || false}
							onChange={handleToggleFilter}
						/>

						<CheckboxOption
							name="employees250To499"
							label="250-499"
							checked={appliedFilters?.employees250To499 || false}
							onChange={handleToggleFilter}
						/>

						<CheckboxOption
							name="employeesOver500"
							label="500+"
							checked={appliedFilters?.employeesOver500 || false}
							onChange={handleToggleFilter}
						/>
					</div>
				</StatusControl>

				<StatusControl title="Square Feet" icon={SquareFeetIcon}>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<CheckboxOption
							name="sqftSmall"
							label="< 2,500"
							checked={appliedFilters?.sqftSmall || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="sqftMedium"
							label="2,500-10,000"
							checked={appliedFilters?.sqftMedium || false}
							onChange={handleToggleFilter}
						/>
						<CheckboxOption
							name="sqftLarge"
							label="> 10,000"
							checked={appliedFilters?.sqftLarge || false}
							onChange={handleToggleFilter}
						/>
					</div>
				</StatusControl>
			</FilterComponents.Content>
		</FilterComponents.Wrapper>
	);
};

type StatusControlProps = ComponentProps<'section'> & {
	title: string;
	icon?: FunctionComponent<ComponentProps<'svg'>>;
};

const StatusControl = ({
	title,
	icon: Icon,
	children,
	...props
}: StatusControlProps) => (
	<section
		{...props}
		className="flex flex-col gap-3 border-b border-dm-strong-mid-grey pb-4"
	>
		<p className="flex items-center gap-2 text-sm text-dm-white">
			{Icon && <Icon className="h-4 w-4" />} {title}
		</p>
		{children}
	</section>
);

type ChangeFiltersProps = {
	name: string;
	value: 'true' | 'false';
};

type FilterOptionProps = ChangeFiltersProps & {
	label: string;
	icon: FunctionComponent<ComponentProps<'svg'>>;
	onChange: (filter: ChangeFiltersProps) => void;
	checked: boolean;
};

const CheckboxOption = ({
	name,
	label,
	checked,
	onChange
}: Omit<FilterOptionProps, 'value' | 'icon'>) => {
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		onChange({
			name,
			value: e.currentTarget.checked ? 'true' : 'false'
		});
	};

	return (
		<label className="flex w-full max-w-[40%] cursor-pointer items-center gap-2 text-xs text-white">
			<Input
				type="checkbox"
				size="sm"
				name={name}
				onChange={handleChange}
				className="[&:has(input:checked)_path]:fill-dm-gold"
				checked={checked}
			/>

			<span className="flex items-center gap-1">{label}</span>
		</label>
	);
};

const RadioOption = ({
	name,
	value,
	label,
	icon: Icon,
	checked,
	onChange
}: FilterOptionProps) => {
	const handleChange = () => {
		onChange({
			name,
			value
		});
	};

	return (
		<label className="flex cursor-pointer items-center gap-2 text-sm text-white">
			<Input
				type="radio"
				size="sm"
				name={name}
				value={value}
				onChange={handleChange}
				checked={checked}
			/>

			<span className="flex items-center gap-1">
				<Icon width={12} height={12} />
				{label}
			</span>
		</label>
	);
};
